/*
 * Purpose: Mission integration discovery UI — web-found APIs → approve for tenant library
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { ExternalLink, Globe, Plug, RefreshCw, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIntegrationDiscoveryStore } from '@/stores/integrationDiscoveryStore';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { pricingTierLabel, resolveProposalPricingTier } from '@/lib/integrationPricing';
import { cn } from '@/lib/utils';

interface MissionIntegrationDiscoveryPanelProps {
  outcome: string;
  interpretedGoal: string;
  gaps?: string[];
  className?: string;
}

export function MissionIntegrationDiscoveryPanel({
  outcome,
  interpretedGoal,
  gaps = [],
  className,
}: MissionIntegrationDiscoveryPanelProps) {
  const missionSessionId = useMissionKnowledgeStore((s) => s.missionSessionId);
  const proposals = useIntegrationDiscoveryStore((s) => s.proposals);
  const isDiscovering = useIntegrationDiscoveryStore((s) => s.isDiscovering);
  const lastSearchProvider = useIntegrationDiscoveryStore((s) => s.lastSearchProvider);
  const error = useIntegrationDiscoveryStore((s) => s.error);
  const discoverForMission = useIntegrationDiscoveryStore((s) => s.discoverForMission);
  const approveProposal = useIntegrationDiscoveryStore((s) => s.approveProposal);
  const dismissProposal = useIntegrationDiscoveryStore((s) => s.dismissProposal);

  const visible = proposals.filter((p) => p.status === 'proposed');

  return (
    <div className={cn('space-y-3 rounded-md border border-border/60 bg-background/80 p-3', className)}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Globe className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground">Integration discovery</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              AutoBlocks searches for APIs and plugins with <strong>free tiers first</strong>, converts them to
              safe AutoBlocks scaffolds, and saves approved ones to your{' '}
              <strong>workspace library</strong> for later Missions (tenant-scoped, not global).
            </p>
            {lastSearchProvider && (
              <Badge variant="outline" className="text-[10px] mt-1">
                Search: {lastSearchProvider}
              </Badge>
            )}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="shrink-0 h-8 text-xs"
          disabled={isDiscovering || !missionSessionId}
          onClick={() =>
            void discoverForMission({
              missionSessionId,
              outcome,
              interpretedGoal,
              gaps,
            })
          }
        >
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1', isDiscovering && 'animate-spin')} />
          {isDiscovering ? 'Searching…' : 'Discover integrations'}
        </Button>
      </div>

      {error && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">{error}</p>
      )}

      {visible.length === 0 && !isDiscovering && (
        <p className="text-[11px] text-muted-foreground">
          No proposals yet — run discovery to find APIs that fit this Mission.
        </p>
      )}

      <ul className="space-y-2">
        {visible.map((p) => {
          const tier = resolveProposalPricingTier(p);
          return (
          <li
            key={p.id}
            className="rounded-md border border-primary/20 bg-primary/[0.03] px-3 py-2 text-xs space-y-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Plug className="h-3.5 w-3.5 text-primary" aria-hidden />
              <span className="font-medium text-foreground">{p.name}</span>
              <Badge
                variant={tier === 'free' ? 'default' : 'outline'}
                className={cn(
                  'text-[10px]',
                  tier === 'free' && 'bg-emerald-600 hover:bg-emerald-600',
                  tier === 'freemium' && 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400'
                )}
              >
                {pricingTierLabel(tier)}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {p.connector.auth}
              </Badge>
              <Badge variant="secondary" className="text-[10px]">
                scaffold
              </Badge>
            </div>
            <p className="text-muted-foreground">{p.description}</p>
            {p.safetyNotes.length > 0 && (
              <ul className="space-y-0.5 text-[10px] text-muted-foreground">
                {p.safetyNotes.map((n) => (
                  <li key={n} className="flex gap-1.5">
                    <Shield className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
                    {n}
                  </li>
                ))}
              </ul>
            )}
            {p.sourceUrl && (
              <a
                href={p.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
              >
                {p.sourceTitle || 'Source docs'}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" size="sm" className="h-7 text-[11px]" onClick={() => approveProposal(p.id)}>
                Save to workspace library
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-[11px]"
                onClick={() => dismissProposal(p.id)}
              >
                Dismiss
              </Button>
            </div>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
